/**
 * 계약 관련 API 훅
 * 계약 정보의 조회, 수정, 저장 등을 처리하는 커스텀 훅입니다.
 */

import _ from "lodash";
import moment from "moment";
import { useCallback } from "react";
import { BillingDetail } from "src/api/billingSchedule/billing-type";
import { getScheduleDetail_RCB01, getScheduleRoundNOverdueList, patchScheduleDetail_RC002 } from "src/api/billingSchedule/billingSchedule-api";
import {
  createContractAsync,
  getContractMagage_RCA25,
  getContractProductSnapshotDetailPopup,
  getContract_RCA27,
  getExpenseProofRequest,
  makeQuotation_TIQ02,
  newMakeContract_RCA02,
  patchContractManage_RCA26,
  patchContract_RCA28,
  setExpenseProofRequest,
} from "src/api/contract/contract-api";
import {
  ContractDetail,
  ContractManagaDetail,
  ContractRequest,
  ContractorNoti,
  DetailTotalData,
  NewContractSaveModel,
  SiblingContract,
} from "src/api/contract/contract-types";
import { useApiOperation } from "src/api/hooks";
import { getOrgDetail } from "src/api/organization/org-api";
import { MbOrgDetail, MbOrgMedia } from "src/api/organization/org-types";
import { getProductDetailAsync } from "src/api/product/product-api";
import { providerDetailAsync } from "src/api/provider/provider-api";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import { ViewYmdFormat, YmdFormat } from "src/utils";
import { useS2Contract } from "./useS2Contract";

/**
 * 두 객체 간의 차이를 찾는 유틸리티 함수
 * @param obj1 - 비교할 첫 번째 객체
 * @param obj2 - 비교할 두 번째 객체
 * @returns 두 객체 간에 다른 키들의 배열
 */
export const objDiff = <T extends Record<string, any>>(
  obj1: T,
  obj2: T, // 같은 타입 T
): Array<keyof T> => {
  let diff: Array<keyof T> = [];
  if (obj1 && obj2) {
    diff = Object.keys(obj1).reduce((result, key) => {
      if (!obj2.hasOwnProperty(key)) {
        result.push(key);
      } else if (_.isEqual(JSON.stringify(obj1[key]), JSON.stringify(obj2[key]))) {
        const resultKeyIndex = result.indexOf(key);
        result.splice(resultKeyIndex, 1);
      }
      return result;
    }, Object.keys(obj2));
  }
  return diff;
};

/**
 * 시간 값을 체크하고 형식을 변환하는 유틸리티 함수
 * @param key - 시간 필드의 키 값
 * @param value - 변환할 날짜/시간 값
 * @returns 형식이 변환된 시간 값
 */
const timeCheckNChange = (key: string, value: Date) => {
  let changeValues: any = value;

  if (key.indexOf("StartTime") > -1 || key.indexOf("Date") > -1) {
    changeValues = moment(value).format(YmdFormat.WITH_TIME_ZONE);
  }

  if (key.indexOf("EndTime") > -1) {
    let useEndTime = moment(value, "YYYY-MM-DD").format(YmdFormat.YYYY_MM_DD) + " 23:59:59.999";
    changeValues = moment(useEndTime).format(YmdFormat.WITH_TIME_ZONE);
  }

  return changeValues;
};

/**
 * 배열에서 cmdType이 있는 항목만 필터링하는 유틸리티 함수
 * @param value - 필터링할 배열
 * @returns cmdType이 있는 항목들의 배열
 */
const arrayCmdTypeChecker = (value: Array<any>) => {
  let rtnArr: any = [];

  rtnArr = value!.filter((obj: any) => obj.cmdType !== undefined);

  return rtnArr;
};

/**
 * 데이터 변경 사항을 체크하고 업데이트 데이터를 생성하는 유틸리티 함수
 * @param rtnData - 반환할 데이터 객체
 * @param original - 원본 데이터
 * @param changeData - 변경된 데이터
 * @param makeData - 생성할 데이터 객체
 * @returns 유효성 검사 결과와 생성된 데이터
 */
export const checkDataNMake = (rtnData: any, original: any, changeData: any, makeData: any) => {
  if (changeData !== undefined && _.isEqual(original, changeData)) {
    rtnData.validationPass = false;
    rtnData.message = "변경사항이 없습니다.";
  } else {
    rtnData.validationPass = true;
    rtnData.message = "변경사항이 있습니다.";
    const diffKeys = objDiff(original, changeData);

    if (diffKeys.length > 0) {
      for (let i = 0; i < diffKeys.length; i++) {
        const key = diffKeys[i];
        let getDatas;
        if (Array.isArray(changeData[key])) {
          getDatas = arrayCmdTypeChecker(changeData[key]);
        } else if (typeof key === "string") {
          getDatas = timeCheckNChange(key, changeData[key]);
        }
        makeData[key] = getDatas;
      }
    }
  }
  return { rtnData, makeData };
};

/**
 * 계약 관련 API 호출을 위한 커스텀 훅
 * 계약 정보의 조회, 수정, 저장 등의 기능을 제공합니다.
 */
const useContactApi = () => {
  const { isS2Contract } = useS2Contract();
  const { fetchAuthorityReadonly } = usePartnerAuthority();

  const { executeAsync: getContractDetailAll } = useApiOperation(getContract_RCA27);
  const { executeAsync: getContractManageDetailAll } = useApiOperation(getContractMagage_RCA25);
  const { executeAsync: getScheduleListAll } = useApiOperation(getScheduleDetail_RCB01);
  const { executeAsync: getSpaceProduct } = useApiOperation(getProductDetailAsync, { noErrorModal: true });
  const { executeAsync: getProviderDetail } = useApiOperation(providerDetailAsync);
  const { executeAsync: getSnapshot } = useApiOperation(getContractProductSnapshotDetailPopup);
  const { executeAsync: getOrganization } = useApiOperation(getOrgDetail);
  const { executeAsync: getRoundList } = useApiOperation(getScheduleRoundNOverdueList, { noLoading: true });
  const { executeAsync: editSchedule } = useApiOperation(patchScheduleDetail_RC002);
  const { executeAsync: editContract } = useApiOperation(patchContract_RCA28);
  const { executeAsync: editContractManage } = useApiOperation(patchContractManage_RCA26);
  const { executeAsync: saveContractAll } = useApiOperation(newMakeContract_RCA02);
  const { executeAsync: setExpenseProofData } = useApiOperation(setExpenseProofRequest);
  const { executeAsync: getExpenseProofData } = useApiOperation(getExpenseProofRequest);
  const { executeAsync: saveQuotation } = useApiOperation(makeQuotation_TIQ02);

  // 신청계약 등록생성
  const { executeAsync: createContract } = useApiOperation(createContractAsync);

  // 신청계약 등록
  const createContractData = async (contractRequest: ContractRequest) => {
    const { data } = await createContract(contractRequest);
    return data.data;
  };

  // 신청계약 등록생성
  /**
   * 스케줄의 라운드 목록을 조회하는 함수
   * @param scheduleId - 스케줄 ID
   * @returns 라운드 목록
   */
  const getSchedulesRounds = useCallback(
    async (scheduleId: string) => {
      const findRounds = await getRoundList({ scheduleId: Number(scheduleId) });
      return findRounds.data.data.content || [];
    },
    [getRoundList],
  );

  /**
   * 견적서를 저장하는 함수
   * @param quotation - 견적서 데이터
   * @returns 저장된 견적서 정보
   */
  const sendQuoTation = async (quotation: any) => {
    const response = await saveQuotation(quotation);

    if (response.data.data.content) {
      return response.data.data.content;
    }
  };

  /**
   * 상품 정보를 조회하는 함수
   * @param productId - 상품 ID
   * @returns 상품 상세 정보
   */
  const getProductData = useCallback(
    async (productId: number) => {
      if (productId) {
        let productDetail;
        const productResponse: any = await getSpaceProduct({ productId: Number(productId) });
        if (productResponse.data.data && productResponse.data.data.content) {
          productDetail = productResponse?.data?.data?.content;
        }

        return productDetail;
      }
    },
    [getSpaceProduct],
  );

  /**
   * 프로바이더 정보를 조회하는 함수
   * @param providerId - 프로바이더 ID
   * @returns 프로바이더 상세 정보
   */
  const getProviderData = useCallback(
    async (providerId: number) => {
      //프로바이더 정보 조회
      const response: any = await getProviderDetail({ providerId: String(providerId) });
      return response.data.data;
    },
    [getProviderDetail],
  );

  /**
   * 계약의 스냅샷 데이터를 조회하는 함수
   * @param contractId - 계약 ID
   * @returns 스냅샷 데이터
   */
  const getSnapShotData = useCallback(
    async (contractId: number) => {
      let productData: any = {};

      const snapShotResponse: any = await getSnapshot({ contractId: Number(contractId) });

      const filteredProduct = snapShotResponse.data.data.content.filter((product: any) => product.domain === "product");
      const filteredBuildings = snapShotResponse.data.data.content.filter((product: any) => product.domain === "building") || [];
      const rentalCost = snapShotResponse.data.data.content.find(
        (product: any) => product.domain === "rentalCost" || product.domain === "productionPrice",
      );

      filteredProduct.forEach((pr: any) => {
        productData[pr.attrName] = pr.attrValue;
        productData.id = pr.domainId;
      });
      if (filteredBuildings) {
        productData.buildings = filteredBuildings;
      }
      if (rentalCost) {
        productData.productionPrice = rentalCost.attrValue;
      }

      return productData;
    },
    [getSnapshot],
  );

  /**
   * 계약 상세 정보를 조회하는 함수
   * @param contractId - 계약 ID
   * @returns 계약 상세 정보
   */
  const getContractDetailData = useCallback(
    async (contractId: string) => {
      return await getContractDetailAll({ id: Number(contractId) });
    },
    [getContractDetailAll],
  );

  const getContractDetail = async (contractId: string) => {
    // const callProductSteps: Array<string> = [ContractStep.APPLY_RECEIVED, ContractStep.APPLY_CONFIRM, ContractStep.CONTRACT_PLAN];

    let makeDetailDatas: DetailTotalData = {};
    let contractDetail: ContractDetail = {};
    let contractManage: ContractManagaDetail = {};
    let contracts: Array<SiblingContract> = [];
    let scheduleList: Array<BillingDetail> = [];
    let billContactList: Array<ContractorNoti> = [];

    const contractDetailResponse = await getContractDetailData(contractId);

    contractDetail = { ...contractDetailResponse.data.data.contract };
    makeDetailDatas.contract = contractDetail;

    const contractManageDetailResponse: any = await getContractManageDetailAll({ id: Number(contractDetail.contractManageId) }).catch(
      () => undefined,
    );
    if (contractManageDetailResponse) {
      contractManage = { ...contractManageDetailResponse.data.data.contractManage };
      // 같은 계약관리에 묶인 전체 계약 리스트 (재계약 1:N). 계약시작일 오름차순으로 정렬해서 내려준다
      contracts = _.sortBy(contractManageDetailResponse.data.data.contracts || [], (contract: SiblingContract) =>
        moment(contract.contractStartTime).valueOf(),
      );
    }
    makeDetailDatas.contract = contractDetail;
    makeDetailDatas.contractManage = contractManage;
    makeDetailDatas.contracts = contracts;

    //사업자 정보가 있는 경우 조회한다.
    if (Number(makeDetailDatas.contractManage.mbOrganizationId) > 0 && Number(makeDetailDatas.contractManage.mbOrganizationId) < 99999) {
      const response: any = await getOrganization({ id: Number(makeDetailDatas.contractManage.mbOrganizationId) }).catch(() => undefined);

      if (response) {
        makeDetailDatas.contractOrganization = response.data.data;
      }
    }
    const findExpenseProofData = await getExpenseProofData({ contractId }).catch(() => undefined);
    if (findExpenseProofData) {
      if (findExpenseProofData.data.data.expenseProofRequest.contractId === null) {
        makeDetailDatas.expenseProofData = null;
      } else {
        makeDetailDatas.expenseProofData = findExpenseProofData.data.data.expenseProofRequest;
      }
    }
    const schedules: any = await getScheduleListAll({ contractId: Number(contractId) });

    scheduleList = scheduleList.concat(schedules.data.data.scheduleList);
    makeDetailDatas.scheduleList = scheduleList;
    billContactList = schedules.data.data.billContactList;
    makeDetailDatas.billContactList = billContactList;
    makeDetailDatas.provider = await getProviderData(Number(makeDetailDatas.contractManage.providerId));

    //계약의 스냅샷 정보 조회
    makeDetailDatas.product = await getSnapShotData(Number(makeDetailDatas.contract.contractId));

    // 계약 S2 페이지인데 TIME_COURT 상품이 아니면 권한 체크 후 이전 페이지로 이동
    if (isS2Contract && makeDetailDatas.product.productType !== "TIME_COURT") {
      fetchAuthorityReadonly(true, true);
      return {};
    }
    return makeDetailDatas;
  };

  const getOrganizationData = useCallback(
    async (mbOrganizationId: Number) => {
      if (mbOrganizationId) {
        const response: any = await getOrganization({ id: Number(mbOrganizationId) });
        const mbOrgDetail: MbOrgDetail = response.data.data;

        const proofs = mbOrgDetail?.mediaList!.filter((media: MbOrgMedia) => media.type === "PROOF");
        const bis = mbOrgDetail?.mediaList!.filter((media: MbOrgMedia) => media.type !== "PROOF");

        return { organization: mbOrgDetail.organization, proofs, bis };
      }
    },
    [getOrganization],
  );

  const checkOverdueList = useCallback(
    async (scheduleId: number) => {
      let checkOverdueBill = [];
      let payCheck = [];
      const res: any = await getRoundList({ scheduleId });
      const billRounds = res.data.data.content;
      const today = moment().format(ViewYmdFormat.YYYY_MM_DD);

      if (billRounds) {
        checkOverdueBill = billRounds.filter((round: any) => round.bill.isOverdueBill);
        payCheck = billRounds.filter(
          (round: any) =>
            round.bill.isOverdueBill === false && moment(moment(round.bill.billIssueDate).format(ViewYmdFormat.YYYY_MM_DD)).isBefore(today),
        );
      }

      let pay = false;
      payCheck.forEach((bill: any) => {
        if (bill.bill.billPayStatus !== "PAYMENT_SUCCESS" && bill.bill.billPayStatus !== "PAYMENT_FREE") {
          pay = true;
        }
      });
      let overdue = false;
      checkOverdueBill.forEach((bill: any) => {
        if (bill.bill.billPayStatus !== "PAYMENT_SUCCESS" && bill.bill.billPayStatus !== "PAYMENT_FREE") {
          overdue = true;
        }
      });

      //TODO 계약일시 입력 상태인지 체크
      if (pay !== false || overdue !== false) {
        return false;
      }
      return true;
    },
    [getRoundList],
  );

  const saveContractBillData = useCallback(
    async (original: BillingDetail, changeData: BillingDetail) => {
      let rtnData = { validationPass: false, message: "", response: {} };
      let makeSaveSchedule: BillingDetail = {};

      const check: any = checkDataNMake(rtnData, original, changeData, makeSaveSchedule);
      rtnData = { ...check.rtnData };
      makeSaveSchedule = check.makeData;

      //API 전송
      if (rtnData.validationPass && Object.keys(makeSaveSchedule).length > 0) {
        makeSaveSchedule.scheduleId = changeData.scheduleId;
        rtnData.response = await editSchedule({ contractId: Number(original.contractId), scheduleList: [makeSaveSchedule] });
      }

      return rtnData;
    },
    [editSchedule],
  );

  const editContractData = useCallback(
    async (original: ContractDetail, changeData: ContractDetail) => {
      let rtnData = { validationPass: false, message: "", response: {} };
      let makeSaveContract: ContractDetail = {};

      const check: any = checkDataNMake(rtnData, original, changeData, makeSaveContract);
      rtnData = { ...check.rtnData };
      makeSaveContract = check.makeData;

      //API 전송
      if (rtnData.validationPass && Object.keys(makeSaveContract).length > 0) {
        makeSaveContract.contractId = original.contractId;
        rtnData.response = await editContract({ id: Number(original.contractId), contract: makeSaveContract });
      }

      return rtnData;
    },
    [editContract],
  );

  const editContractManageData = useCallback(
    async (original: ContractManagaDetail, changeData: ContractManagaDetail) => {
      let rtnData = { validationPass: false, message: "", response: {} };
      let makeSaveContractManage: ContractManagaDetail = {};

      const check: any = checkDataNMake(rtnData, original, changeData, makeSaveContractManage);
      rtnData = { ...check.rtnData };
      makeSaveContractManage = check.makeData;

      //API 전송
      if ((rtnData.validationPass, Object.keys(makeSaveContractManage).length > 0)) {
        makeSaveContractManage.contractManageId = original.contractManageId;
        rtnData.response = await editContractManage({ id: Number(original.contractManageId), contractManage: makeSaveContractManage });
      }

      return rtnData;
    },
    [editContractManage],
  );

  const saveFirstContractData = async (saveData: NewContractSaveModel) => {
    let rtnData = { validationPass: false, message: "", response: {} };
    if (saveData) {
      rtnData.response = await saveContractAll({ saveModel: saveData });
    }
    return rtnData;
  };

  return {
    getContractDetailData,
    getContractDetail,
    checkOverdueList,
    editContractData,
    saveContractBillData,
    editContractManageData,
    getOrganizationData,
    getProviderData,
    getSnapShotData,
    getProductData,
    saveFirstContractData,
    getContractManageDetailAll,
    sendQuoTation,
    getSchedulesRounds,
    createContractData,
  };
};
export default useContactApi;
